import { Component, Element, State, Watch, forceUpdate, h } from "@stencil/core";
import { CreateCaseResponse, TRANSACTION_STATES } from "../../api/createCase";
import { ApiClient } from "../../utils/ApiClient";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";

@Component({
    tag: "ux-f-sis-create-case-connector",
    styleUrl: "sis-connector.css",
    shadow: true
})
export class SisConnector {
    @Element() host: HTMLUxFSisCreateCaseConnectorElement;

    @State() response: CreateCaseResponse = null;
    @State() errorMessage: string = null;
    @State() isRequestPending = false;
    @State() locale: string;

    private parent: HTMLMfCreateServiceCallElement = null;
    private apiClient: ApiClient = null;
    private i18nSubscriberId: string;

    private onServiceCallSubmit = async (event: CustomEvent) => {
        this.isRequestPending = true;
        if (this.apiClient === null) {
            this.apiClient = new ApiClient(this.parent?.nonProd ? "non-prod" : "prod");
            await this.apiClient.requestTbdbToken();
        }
        this.apiClient
            .request<CreateCaseResponse>("/servicecall/createcase", event.detail)
            .then((response) => {
                this.response = response;
            })
            .catch((error) => {
                this.errorMessage = error;
            })
            .finally(() => {
                this.isRequestPending = false;
            });
    };

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.parent = this.host.closest("mf-create-service-call");
        this.parent?.addEventListener("mfServiceCallSubmit", this.onServiceCallSubmit);
        this.locale = this.parent?.locale;
    }

    disconnectedCallback() {
        this.parent?.removeEventListener("mfServiceCallSubmit", this.onServiceCallSubmit);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    render() {
        if (this.isRequestPending) {
            return <ux-loader variant={"spinner"}></ux-loader>;
        }
        if (this.response === null && this.errorMessage === null) {
            return null;
        }
        const t = (key: TranslationKey, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const formatTime = (time: string) => `<ux-date-time utc="${time}" format="${t("timeFormat")}"></ux-date-time>`;
        if (this.response?.[0]?.transactionState === TRANSACTION_STATES.RECEIVED) {
            const serviceHours = this.response?.[0]?.serviceHours;
            const hasMonFri = serviceHours?.MonFriStart && serviceHours?.MonFriEnd;
            const hasSat = serviceHours?.SatStart && serviceHours?.SatEnd;
            const monFriHtml = hasMonFri
                ? t("sisConnector.success.serviceTimes.MonFri", {
                      startTime: formatTime(serviceHours.MonFriStart),
                      endTime: formatTime(serviceHours.MonFriEnd)
                  })
                : "";
            const satHtml = hasSat
                ? t("sisConnector.success.serviceTimes.Sat", {
                      startTime: formatTime(serviceHours.SatStart),
                      endTime: formatTime(serviceHours.SatEnd)
                  })
                : "";
            // TODO: add texts for service hotline and technical guides

            return (
                <div>
                    <h1>{t("sisConnector.success.headline")}</h1>
                    {this.response[0]?.isCustomerOrderNumberRequired && <p>{t("sisConnector.success.orderNumberRequired")}</p>}
                    {this.response[0]?.isOutsideOfServiceHours && (
                        <div>
                            <p>{t("sisConnector.success.outsideOfServiceHours")}</p>
                            {hasMonFri && <p innerHTML={`${monFriHtml} ${satHtml}`} />}
                        </div>
                    )}
                    {this.response[0]?.technicalServiceHotline && (
                        <p>
                            {t("sisConnector.success.withTechnicalServiceHotline", {
                                hotline: this.response[0]?.technicalServiceHotline
                            })}
                        </p>
                    )}
                    {!this.response[0]?.technicalServiceHotline && (
                        <p>{t("sisConnector.success.withoutTechnicalServiceHotline")}</p>
                    )}
                </div>
            );
        } else {
            return (
                <div>
                    <h1>{t("sisConnector.failed.headline")}</h1>
                    <p>{this.errorMessage || t("sisConnector.failed.unknownError")}</p>
                </div>
            );
        }
    }
}
